/**
 * create-order-dialog.tsx — Nút "Tạo đơn" cạnh "Sản phẩm" ở khung chat: tạo đơn trên Evotech cho khách của hội thoại
 * theo luồng POS của Evotech (phiên thanh toán): chọn sản phẩm, nhận tại cửa hàng / giao tận nơi, thanh toán, VOUCHER, TRỪ ĐIỂM,
 * ghi chú — giá / giảm / phí ship do Evotech tính — rồi gửi xác nhận cho khách. Đóng popup không tạo đơn → huỷ phiên (không treo ở POS).
 * Backend: modules/chat/chat-evotech-order-routes.ts.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import {
  AlertTriangle, CheckCircle2, Coins, ImageOff, Loader2, MapPin, Minus, PackagePlus, Plus, ReceiptText, Search, Store, Ticket, Trash2, Truck,
  UserRound, X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch, Textarea } from '@/components/ui/misc'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ComposerToolTrigger, type ToolTriggerMode } from '@/components/shared/composer-tool-trigger'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { resolveImageUrl, useProductPickerList, useProductPickerSources, type Product } from '@/hooks/use-products'
import {
  cancelEvotechCheckout, useCollectVoucher, useCreateEvotechOrder, useEvotechCheckout, useEvotechOrderOptions, useEvotechPlaces, useEvotechVouchers,
  type CheckoutQuote, type CreatedEvotechOrder, type DeliveryType, type EvotechOrderDraft, type EvotechOrderOptions, type NewAddress,
  type SessionHolder, type VoucherOption,
} from '@/hooks/use-evotech-order'
import { stockLabel } from './product-picker-lib'

const NEW_ADDRESS = '__new__'

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** Hiện khi tổ chức đã kết nối Evotech. */
export function CreateOrderButton({ convId, contactId, mode }: { convId: string; contactId?: string | null; mode?: ToolTriggerMode }) {
  const { t } = useTranslation('convCommerce')
  const { data: meta } = useProductPickerSources(true)
  const [open, setOpen] = useState(false)
  // Phiên thanh toán Evotech của lần mở popup này; đóng mà chưa tạo đơn → huỷ phiên.
  const holder = useRef<SessionHolder>({ id: null, chain: Promise.resolve() })
  const onOpenChange = (o: boolean) => {
    if (!o) {
      cancelEvotechCheckout(convId, holder.current.id)
      holder.current = { id: null, chain: Promise.resolve() }
    }
    setOpen(o)
  }
  if (!meta?.evotech.connected) return null
  return (
    <>
      {mode ? (
        <ComposerToolTrigger
          mode={mode}
          icon={PackagePlus}
          label={t('order.button')}
          disabled={!contactId}
          title={contactId ? t('order.tooltip.create') : t('order.tooltip.noContact')}
          onClick={() => setOpen(true)}
        />
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 px-2.5 text-xs"
          disabled={!contactId}
          title={contactId ? t('order.tooltip.create') : t('order.tooltip.noContact')}
          aria-label={t('order.button')}
          onClick={() => setOpen(true)}
        >
          <PackagePlus className="h-4 w-4 text-primary" />
          {t('order.button')}
        </Button>
      )}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[min(92vh,860px)] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:rounded-xl">
          {open && <CreateOrderBody convId={convId} contactId={contactId ?? null} holder={holder.current} onClose={() => onOpenChange(false)} />}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface Line {
  product: Product
  quantity: number
}

function CreateOrderBody({ convId, contactId, holder, onClose }: { convId: string; contactId: string | null; holder: SessionHolder; onClose: () => void }) {
  const { t } = useTranslation('convCommerce')
  const opts = useEvotechOrderOptions(convId, true)
  const create = useCreateEvotechOrder(convId, contactId)
  const [lines, setLines] = useState<Line[]>([])
  const [delivery, setDelivery] = useState<DeliveryType>('InShop')
  const [branchId, setBranchId] = useState<string | null>(null)
  const [addressChoice, setAddressChoice] = useState<string | null>(null)
  const [newAddress, setNewAddress] = useState<NewAddress>({ fullName: '', phoneNumber: '', provinceId: '', wardId: '', address: '' })
  const [typePay, setTypePay] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [sendConfirmation, setSendConfirmation] = useState(true)
  const [voucherCodes, setVoucherCodes] = useState<string[]>([])
  const [usePoints, setUsePoints] = useState(false)
  const [done, setDone] = useState<CreatedEvotechOrder | null>(null)

  // Giá trị mặc định khi tải xong lựa chọn: chi nhánh mặc định, phương thức hợp lệ đầu tiên, địa chỉ mặc định.
  const o = opts.data
  useEffect(() => {
    if (!o) return
    setBranchId((v) => v ?? o.branches[0]?.branchId ?? null)
    setTypePay((v) => v ?? o.payments.find((p) => p.allowed)?.typePay ?? null)
    setAddressChoice((v) => v ?? (o.addresses[0]?.shippingAddressId ?? NEW_ADDRESS))
    setNewAddress((a) => (a.fullName || a.phoneNumber ? a : { ...a, fullName: o.contact.name ?? '', phoneNumber: o.contact.phone ?? '' }))
  }, [o])

  // Mã voucher phí ship (chỉ dùng khi giao tận nơi) — để bỏ khi chuyển sang nhận tại cửa hàng.
  const shipCodes = useRef(new Set<string>())
  const draft: EvotechOrderDraft | null = useMemo(() => {
    if (!lines.length || !typePay) return null
    const express = delivery === 'ExpressDelivery'
    const useNew = express && addressChoice === NEW_ADDRESS
    return {
      items: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity })),
      delivery,
      branchId,
      addressId: express && !useNew ? addressChoice : null,
      newAddress: useNew ? newAddress : null,
      typePay,
      notes: notes.trim() || null,
      voucherCodes: express ? voucherCodes : voucherCodes.filter((c) => !shipCodes.current.has(c)),
      usePoints,
    }
  }, [lines, delivery, branchId, addressChoice, newAddress, typePay, notes, voucherCodes, usePoints])

  // Tính lại trên phiên Evotech: bỏ ghi chú / địa chỉ mới (không ảnh hưởng giá) để không gọi lại khi đang gõ.
  const quoteDraft = useDebounced(draft ? { ...draft, notes: null, newAddress: null } : null, 450)
  const checkout = useEvotechCheckout(convId, quoteDraft, holder)
  const session = checkout.data?.session
  const quote = session?.quote
  const hasCustomer = !!checkout.data?.hasCustomer
  const vouchers = useEvotechVouchers(convId, session?.sessionId ?? null, hasCustomer && lines.length > 0)
  useEffect(() => {
    shipCodes.current = new Set((vouchers.data?.vouchers ?? []).filter((v) => v.kind === 'Transport').map((v) => v.code))
  }, [vouchers.data])

  // Evotech bỏ voucher không áp được (chưa đủ đơn tối thiểu, hết hạn…) → bỏ khỏi lựa chọn + báo.
  const dropped = checkout.data?.droppedVoucherCodes
  useEffect(() => {
    if (!dropped?.length) return
    setVoucherCodes((cs) => cs.filter((c) => !dropped.includes(c)))
    toast.warning(t('order.voucher.dropped', { codes: dropped.join(', ') }))
  }, [dropped, t])

  const toggleVoucher = (v: VoucherOption) =>
    setVoucherCodes((cs) => {
      if (cs.includes(v.code)) return cs.filter((c) => c !== v.code)
      // Như POS: một voucher giảm giá / quà + một voucher phí ship.
      const all = vouchers.data?.vouchers ?? []
      const sameGroup = (c: string) => {
        const k = all.find((x) => x.code === c)?.kind ?? 'Promotion'
        return (k === 'Transport') === (v.kind === 'Transport')
      }
      return [...cs.filter((c) => !sameGroup(c)), v.code]
    })

  const addProduct = (p: Product) =>
    setLines((ls) => (ls.some((l) => l.product.id === p.id) ? ls.map((l) => (l.product.id === p.id ? { ...l, quantity: Math.min(9999, l.quantity + 1) } : l)) : [...ls, { product: p, quantity: 1 }]))
  const setQty = (id: string, q: number) => setLines((ls) => ls.map((l) => (l.product.id === id ? { ...l, quantity: Math.max(1, Math.min(9999, q || 1)) } : l)))
  const remove = (id: string) => setLines((ls) => ls.filter((l) => l.product.id !== id))

  const blocked = o?.customer.state === 'blocked'
  const noPayment = !!o && !o.payments.some((p) => p.allowed)
  const addrIncomplete = delivery === 'ExpressDelivery' && addressChoice === NEW_ADDRESS &&
    (!newAddress.fullName.trim() || !newAddress.phoneNumber.trim() || !newAddress.provinceId || !newAddress.wardId || !newAddress.address.trim())
  const canCreate = !!draft && !!session && !checkout.isFetching && !blocked && !noPayment && !addrIncomplete && !create.isPending

  const submit = () => {
    if (!draft || !holder.id) return
    create.mutate(
      { ...draft, sessionId: holder.id, sendConfirmation },
      {
        onSuccess: (r) => {
          holder.id = null // phiên đã thành đơn — không huỷ khi đóng
          setDone(r)
          toast.success(t('order.created', { orderNo: r.orderNo }))
          if (r.confirmation && !r.confirmation.sent) toast.warning(t('order.confirmNotSentToast', { error: r.confirmation.error ?? t('order.channelError') }))
        },
        onError: (e) => {
          toast.error(apiError(e))
          const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
          if (code === 'CHECKOUT_EXPIRED') holder.id = null // mở phiên mới ở lần tính sau
          void checkout.refetch()
        },
      },
    )
  }

  if (done) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
        <CheckCircle2 className="h-12 w-12 text-success" />
        <div>
          <DialogTitle className="text-lg">{t('order.done.title', { orderNo: done.orderNo })}</DialogTitle>
          <p className="mt-1 text-sm text-muted-foreground">
            <Trans t={t} i18nKey="order.done.totalLine" values={{ total: formatVnd(done.total) }} components={{ b: <b className="text-foreground" /> }} />
            {done.pointsUsed > 0 ? t('order.done.pointsUsed', { points: new Intl.NumberFormat(currentLocale()).format(done.pointsUsed) }) : null}
            {done.total > 0 ? t('order.done.awaitingPayment') : t('order.done.paidInFull')}
          </p>
          {done.confirmation && (
            <p className={cn('mt-1 text-sm', done.confirmation.sent ? 'text-success' : 'text-warning')}>
              {done.confirmation.sent ? t('order.done.confirmSent') : t('order.done.confirmNotSent', { error: done.confirmation.error ?? t('order.channelError') })}
            </p>
          )}
        </div>
        <div className="mt-2 flex gap-2">
          <Button variant="outline" onClick={() => { setDone(null); setLines([]); setNotes(''); setVoucherCodes([]); setUsePoints(false) }}>{t('order.done.createAnother')}</Button>
          <Button onClick={onClose}>{t('common:actions.close')}</Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <DialogHeader className="border-b px-5 py-4 text-left">
        <DialogTitle className="flex items-center gap-2 text-base">
          <PackagePlus className="h-5 w-5 text-primary" /> {t('order.dialogTitle')}
          {o?.shopName && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-normal text-muted-foreground">{o.shopName}</span>}
        </DialogTitle>
        <DialogDescription className="text-xs">{t('order.dialogDescription')}</DialogDescription>
      </DialogHeader>

      {opts.isLoading ? (
        <div className="flex flex-1 items-center justify-center gap-2 p-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t('order.loadingShop')}</div>
      ) : opts.isError || !o ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center text-sm">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p className="text-destructive">{apiError(opts.error)}</p>
          <Button size="sm" variant="outline" onClick={() => opts.refetch()}>{t('common:actions.retry')}</Button>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 overflow-y-auto md:grid-cols-[minmax(0,1fr)_360px] md:overflow-hidden">
          {/* ── Trái: sản phẩm ── */}
          <div className="flex flex-col border-b md:min-h-0 md:border-b-0 md:border-r">
            <ProductSearch onAdd={addProduct} selected={new Set(lines.map((l) => l.product.id))} />
            <div className="border-t md:min-h-0 md:flex-1 md:overflow-y-auto">
              <p className="flex items-center justify-between bg-background/95 px-4 py-2 text-xs font-semibold backdrop-blur md:sticky md:top-0 md:z-10">
                <span>{t('order.linesTitle', { n: lines.length })}</span>
                {lines.length > 0 && <span className="font-normal text-muted-foreground">{t('order.itemsCount', { count: lines.reduce((n, l) => n + l.quantity, 0) })}</span>}
              </p>
              {lines.length === 0 ? (
                <p className="px-4 pb-6 pt-2 text-center text-xs text-muted-foreground"><Trans t={t} i18nKey="order.addHint" components={{ b: <b /> }} /></p>
              ) : (
                <ul className="divide-y">
                  {lines.map((l) => {
                    const itemsId = checkout.data?.lineItems[l.product.id]
                    const q = itemsId ? session?.items.find((x) => x.itemsId === itemsId && !x.isGift) : undefined
                    const unit = q?.unitPrice ?? l.product.price
                    const stock = stockLabel(l.product.stockAvailable)
                    return (
                      <li key={l.product.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
                        <Thumb src={l.product.images[0]} />
                        <div className="min-w-0 flex-1 basis-[calc(100%-3.5rem)] sm:basis-0">
                          <p className="line-clamp-2 text-[13px] font-medium leading-snug">{l.product.name}</p>
                          <p className="mt-0.5 flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                            {l.product.code && <span className="font-mono">{l.product.code}</span>}
                            <span className="tabular-nums">{unit != null ? formatVnd(unit) : t('picker.price.contact')}</span>
                            {stock && <span className={stock.tone === 'out' ? 'text-destructive' : ''}>{stock.text}</span>}
                          </p>
                        </div>
                        <div className="ml-auto flex shrink-0 items-center gap-3 sm:ml-0">
                          <div className="flex shrink-0 items-center rounded-md border">
                            <button type="button" className="px-1.5 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40" onClick={() => setQty(l.product.id, l.quantity - 1)} disabled={l.quantity <= 1} aria-label={t('order.qty.decrease')}>
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <input
                              value={l.quantity}
                              onChange={(e) => setQty(l.product.id, parseInt(e.target.value.replace(/\D/g, ''), 10))}
                              inputMode="numeric"
                              aria-label={t('order.qty.label')}
                              className="w-10 bg-transparent text-center text-sm tabular-nums outline-none"
                            />
                            <button type="button" className="px-1.5 py-1 text-muted-foreground hover:text-foreground" onClick={() => setQty(l.product.id, l.quantity + 1)} aria-label={t('order.qty.increase')}>
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <span className="w-24 shrink-0 text-right text-[13px] font-semibold tabular-nums">{unit != null ? formatVnd(q?.total ?? unit * l.quantity) : '—'}</span>
                          <button type="button" onClick={() => remove(l.product.id)} className="shrink-0 text-muted-foreground hover:text-destructive" aria-label={t('order.removeProduct')}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* ── Phải: khách, nhận hàng, thanh toán, tổng ── */}
          <div className="flex flex-col md:min-h-0">
            <div className="space-y-4 p-4 md:min-h-0 md:flex-1 md:overflow-y-auto">
              <CustomerCard o={o} />

              <section className="space-y-2">
                <Label className="text-xs">{t('order.delivery.label')}</Label>
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
                  {([['InShop', t('order.delivery.inShop'), Store], ['ExpressDelivery', t('order.delivery.express'), Truck]] as const).map(([v, label, Icon]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDelivery(v)}
                      className={cn('flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium', delivery === v ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                    >
                      <Icon className="h-3.5 w-3.5" /> {label}
                    </button>
                  ))}
                </div>
                {o.branches.length > 0 && (
                  <Select value={branchId ?? undefined} onValueChange={setBranchId}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={t('order.branch.placeholder')} /></SelectTrigger>
                    <SelectContent>
                      {o.branches.map((b) => (
                        <SelectItem key={b.branchId} value={b.branchId} className="text-xs">
                          {t(delivery === 'InShop' ? 'order.branch.pickupAt' : 'order.branch.shipFrom', { name: b.name })}{b.isDefault ? t('order.branch.defaultSuffix') : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {delivery === 'ExpressDelivery' && (
                  <AddressPicker
                    convId={convId}
                    o={o}
                    choice={addressChoice}
                    onChoice={setAddressChoice}
                    value={newAddress}
                    onChange={setNewAddress}
                  />
                )}
              </section>

              <section className="space-y-2">
                <Label className="text-xs">{t('order.payment.label')}</Label>
                {noPayment ? (
                  <p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-[11px] text-warning">
                    {t('order.payment.notEnabled')}
                  </p>
                ) : (
                  <Select value={typePay ?? undefined} onValueChange={setTypePay}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue placeholder={t('order.payment.placeholder')} /></SelectTrigger>
                    <SelectContent>
                      {o.payments.map((p) => (
                        <SelectItem key={p.typePay} value={p.typePay} disabled={!p.allowed} className="text-xs">
                          {p.name}{p.allowed ? '' : ` — ${p.reason ?? t('order.payment.unusable')}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </section>

              <VoucherSection
                convId={convId}
                sessionId={session?.sessionId ?? null}
                customerState={o.customer.state}
                hasCustomer={hasCustomer}
                hasItems={lines.length > 0}
                delivery={delivery}
                selected={voucherCodes}
                applied={session?.voucherCodes ?? []}
                list={vouchers.data?.vouchers ?? []}
                reason={vouchers.data?.reason}
                loading={vouchers.isLoading && vouchers.fetchStatus !== 'idle'}
                onToggle={toggleVoucher}
              />

              <PointsSection
                customer={o.customer}
                hasCustomer={hasCustomer}
                quote={quote}
                on={usePoints}
                onChange={setUsePoints}
                disabled={!session}
              />

              <section className="space-y-2">
                <Label htmlFor="evo-order-notes" className="text-xs">{t('order.notes.label')}</Label>
                <Textarea id="evo-order-notes" value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))} placeholder={t('order.notes.placeholder')} className="min-h-[60px] text-xs" />
              </section>
            </div>

            <div className="sticky bottom-0 space-y-3 border-t bg-muted p-4 md:static md:bg-muted/30">
              {session && session.stockIssues.length > 0 && (
                <p className="flex items-center gap-1.5 text-[11px] text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  {t('order.stockIssues', { count: session.stockIssues.length })}
                </p>
              )}
              <Totals lines={lines} quote={quote} loading={checkout.isFetching} error={checkout.isError ? apiError(checkout.error) : null} newAddress={delivery === 'ExpressDelivery' && addressChoice === NEW_ADDRESS} />
              <label className="flex cursor-pointer items-center gap-2 text-xs">
                <Checkbox checked={sendConfirmation} onCheckedChange={(v) => setSendConfirmation(v === true)} />
                {t('order.sendConfirmation')}
              </label>
              <Button className="w-full" onClick={submit} disabled={!canCreate}>
                {create.isPending ? <Loader2 className="animate-spin" /> : <ReceiptText />}
                {create.isPending ? t('order.creating') : t('order.submit')}
              </Button>
              {!lines.length && <p className="text-center text-[11px] text-muted-foreground">{t('order.noLines')}</p>}
              {addrIncomplete && lines.length > 0 && <p className="text-center text-[11px] text-muted-foreground">{t('order.addressIncomplete')}</p>}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function Thumb({ src, className }: { src?: string | null; className?: string }) {
  const url = resolveImageUrl(src)
  return (
    <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40', className)}>
      {url ? <img src={url} alt="" loading="lazy" className="h-full w-full object-cover" /> : <ImageOff className="h-4 w-4 text-muted-foreground/50" />}
    </span>
  )
}

/**
 * Ô tìm sản phẩm: KHÔNG hiện sẵn danh sách — gõ thì kết quả nổi lên (lớp phủ) ngay dưới ô tìm, đè lên danh sách sản phẩm trong đơn;
 * bấm "+" thêm được nhiều món liên tiếp; bấm ra ngoài / Esc / xoá ô tìm thì đóng.
 */
function ProductSearch({ onAdd, selected }: { onAdd: (p: Product) => void; selected: Set<string> }) {
  const { t } = useTranslation('convCommerce')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const q = useDebounced(search.trim(), 300)
  const list = useProductPickerList({ externalSource: 'evotech', search: q || undefined }, !!q)
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data])
  const show = open && !!search.trim()

  useEffect(() => {
    if (!show) return
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    // Popup (Radix Dialog) nghe Esc ở pha capture của document → chặn sớm hơn ở window: Esc chỉ đóng lớp kết quả.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [show])

  return (
    <div ref={box} className="relative z-20 p-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          placeholder={t('order.search.placeholder')}
          className="h-9 pl-8 text-xs"
        />
      </div>
      {show && (
        <div className="absolute inset-x-3 top-full -mt-1 max-h-[min(60vh,440px)] overflow-y-auto rounded-lg border bg-popover shadow-xl">
          {list.isLoading || (search.trim() !== q && !items.length) ? (
            <p className="flex items-center justify-center gap-2 p-4 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('order.search.searching')}</p>
          ) : items.length === 0 ? (
            <p className="p-4 text-center text-xs text-muted-foreground">{t('order.search.none')}</p>
          ) : (
            <ul className="py-1">
              {items.map((p) => {
                const stock = stockLabel(p.stockAvailable)
                const inCart = selected.has(p.id)
                return (
                  <li key={p.id}>
                    <button type="button" onClick={() => onAdd(p)} className="flex w-full items-center gap-3 px-3 py-1.5 text-left hover:bg-muted/60">
                      <Thumb src={p.images[0]} className="h-9 w-9" />
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-1 text-xs font-medium">{p.name}</span>
                        <span className="flex flex-wrap gap-x-2 text-[10.5px] text-muted-foreground">
                          {p.code && <span className="font-mono">{p.code}</span>}
                          {stock && <span className={stock.tone === 'out' ? 'text-destructive' : 'text-success'}>{stock.text}</span>}
                        </span>
                      </span>
                      <span className="shrink-0 text-xs font-semibold tabular-nums">{p.price != null ? formatVnd(p.price) : t('picker.price.contact')}</span>
                      <span
                        className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-full border', inCart ? 'border-primary bg-primary text-primary-foreground' : 'text-primary')}
                        title={inCart ? t('order.search.inCart') : t('order.search.add')}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
          {list.hasNextPage && (
            <div className="border-t p-2 text-center">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                {list.isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />} {t('common:actions.showMore')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function CustomerCard({ o }: { o: EvotechOrderOptions }) {
  const { t } = useTranslation('convCommerce')
  const c = o.customer
  return (
    <section className="rounded-lg border p-3">
      <div className="flex items-start gap-2">
        <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{o.contact.name || t('order.customer.noName')}</p>
          <p className="text-xs text-muted-foreground">{o.contact.phone || t('order.customer.noPhone')}</p>
        </div>
        {c.state === 'linked' && <Badge variant="success" className="shrink-0 text-[10px]">{t('order.customer.linked', { userNo: c.userNo })}</Badge>}
        {c.state === 'found' && <Badge variant="secondary" className="shrink-0 text-[10px]">{t('order.customer.samePhone', { userNo: c.userNo })}</Badge>}
        {c.state === 'new' && <Badge variant="secondary" className="shrink-0 text-[10px]">{t('order.customer.new')}</Badge>}
      </div>
      {c.state === 'found' && <p className="mt-1.5 text-[11px] text-muted-foreground">{t('order.customer.willLink', { who: c.name ? `“${c.name}”` : `#${c.userNo}` })}</p>}
      {c.state === 'new' && <p className="mt-1.5 text-[11px] text-muted-foreground">{t('order.customer.willCreate')}</p>}
      {c.state === 'blocked' && <p className="mt-1.5 text-[11px] text-destructive">{c.reason}</p>}
    </section>
  )
}

function AddressPicker({ convId, o, choice, onChoice, value, onChange }: {
  convId: string
  o: EvotechOrderOptions
  choice: string | null
  onChoice: (v: string) => void
  value: NewAddress
  onChange: (v: NewAddress) => void
}) {
  const { t } = useTranslation('convCommerce')
  const isNew = choice === NEW_ADDRESS
  const provinces = useEvotechPlaces(convId, null, isNew)
  const wards = useEvotechPlaces(convId, value.provinceId || null, isNew && !!value.provinceId)
  return (
    <div className="space-y-2">
      {o.addresses.length > 0 && (
        <div className="space-y-1">
          {o.addresses.map((a) => (
            <label key={a.shippingAddressId} className={cn('flex cursor-pointer items-start gap-2 rounded-md border p-2 text-xs', choice === a.shippingAddressId && 'border-primary bg-primary/5')}>
              <input type="radio" name="evo-addr" className="mt-0.5" checked={choice === a.shippingAddressId} onChange={() => onChoice(a.shippingAddressId)} />
              <span className="min-w-0">
                <span className="font-medium">{[a.fullName, a.phoneNumber].filter(Boolean).join(' · ')}</span>
                {a.isDefault && <span className="ml-1 text-[10px] text-primary">{t('order.address.default')}</span>}
                <span className="block text-muted-foreground">{a.address}</span>
              </span>
            </label>
          ))}
          <label className={cn('flex cursor-pointer items-center gap-2 rounded-md border p-2 text-xs', isNew && 'border-primary bg-primary/5')}>
            <input type="radio" name="evo-addr" checked={isNew} onChange={() => onChoice(NEW_ADDRESS)} />
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {t('order.address.new')}
          </label>
        </div>
      )}
      {isNew && (
        <div className="grid grid-cols-2 gap-2 rounded-md border border-dashed p-2">
          <Input value={value.fullName} onChange={(e) => onChange({ ...value, fullName: e.target.value })} placeholder={t('order.address.recipient')} className="h-8 text-xs" />
          <Input value={value.phoneNumber} onChange={(e) => onChange({ ...value, phoneNumber: e.target.value })} placeholder={t('order.address.phone')} inputMode="tel" className="h-8 text-xs" />
          <Select value={value.provinceId || undefined} onValueChange={(v) => onChange({ ...value, provinceId: v, wardId: '' })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={provinces.isLoading ? t('picker.loading') : t('order.address.province')} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(provinces.data ?? []).map((p) => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={value.wardId || undefined} onValueChange={(v) => onChange({ ...value, wardId: v })} disabled={!value.provinceId}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={wards.isLoading ? t('picker.loading') : t('order.address.ward')} /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(wards.data ?? []).map((w) => <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={value.address} onChange={(e) => onChange({ ...value, address: e.target.value })} placeholder={t('order.address.street')} className="col-span-2 h-8 text-xs" />
          <p className="col-span-2 text-[10.5px] text-muted-foreground">{t('order.address.savedNote')}</p>
        </div>
      )}
    </div>
  )
}

function VoucherSection({
  convId, sessionId, customerState, hasCustomer, hasItems, delivery, selected, applied, list, reason, loading, onToggle,
}: {
  convId: string
  sessionId: string | null
  customerState: EvotechOrderOptions['customer']['state']
  hasCustomer: boolean
  hasItems: boolean
  delivery: DeliveryType
  selected: string[]
  applied: string[]
  list: VoucherOption[]
  reason?: string
  loading: boolean
  onToggle: (v: VoucherOption) => void
}) {
  const { t } = useTranslation('convCommerce')
  const chosen = list.filter((v) => selected.includes(v.code))
  const note = customerState === 'new' || customerState === 'blocked'
    ? t('order.voucher.noAccount')
    : !hasItems
      ? t('order.voucher.addProducts')
      : !hasCustomer
        ? t('order.voucher.attaching')
        : reason ?? null
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-1.5 text-xs"><Ticket className="h-3.5 w-3.5" /> {t('order.voucher.label')}</Label>
        {hasCustomer && hasItems && list.length > 0 && (
          <VoucherPicker convId={convId} sessionId={sessionId} delivery={delivery} selected={selected} list={list} onToggle={onToggle} />
        )}
      </div>
      {note ? (
        <p className="text-[11px] text-muted-foreground">{note}</p>
      ) : loading ? (
        <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> {t('order.voucher.loading')}</p>
      ) : list.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t('order.voucher.none')}</p>
      ) : chosen.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t('order.voucher.available', { count: list.length })}</p>
      ) : null}
      {chosen.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chosen.map((v) => (
            <span
              key={v.code}
              className={cn('inline-flex items-center gap-1 rounded-md border border-dashed px-1.5 py-0.5 text-[11px]', applied.includes(v.code) ? 'border-success/50 text-success' : 'text-muted-foreground')}
            >
              <span className="font-mono">{v.code}</span> · {v.value}
              <button type="button" onClick={() => onToggle(v)} className="ml-0.5 opacity-60 hover:opacity-100" aria-label={t('order.voucher.removeAria', { code: v.code })}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </section>
  )
}

/** Danh sách voucher ở LỚP NỔI (popover) cạnh khung tính tiền — rộng, tìm được, chia nhóm Giảm giá / Phí vận chuyển. */
function VoucherPicker({ convId, sessionId, delivery, selected, list, onToggle }: {
  convId: string
  sessionId: string | null
  delivery: DeliveryType
  selected: string[]
  list: VoucherOption[]
  onToggle: (v: VoucherOption) => void
}) {
  const { t } = useTranslation('convCommerce')
  const collect = useCollectVoucher(convId, sessionId)
  const isMobile = useIsMobile()
  const [q, setQ] = useState('')
  const term = q.trim().toLowerCase()
  const shown = term ? list.filter((v) => `${v.name} ${v.code} ${v.value}`.toLowerCase().includes(term)) : list
  const groups = [
    { title: t('order.voucher.groupDiscount'), items: shown.filter((v) => v.kind !== 'Transport') },
    { title: t('order.voucher.groupShipping'), items: shown.filter((v) => v.kind === 'Transport') },
  ].filter((g) => g.items.length > 0)
  const onCollect = (v: VoucherOption) => {
    if (v.pointCost && !confirm(t('order.voucher.collectConfirm', { name: v.name, points: v.pointCost }))) return
    collect.mutate(v.code, {
      onSuccess: () => {
        toast.success(t('order.voucher.collected', { code: v.code }))
        onToggle(v)
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[11px]">
          <Ticket className="h-3.5 w-3.5" /> {t('order.voucher.choose', { n: list.length })}
        </Button>
      </PopoverTrigger>
      {/* Máy tính: mở sang trái (đè lên cột sản phẩm, không che khu tính tiền); điện thoại: mở phía dưới cho vừa màn. */}
      <PopoverContent side={isMobile ? 'bottom' : 'left'} align={isMobile ? 'end' : 'start'} collisionPadding={12} className="flex max-h-[min(70vh,560px,var(--radix-popover-content-available-height))] w-[min(440px,calc(100vw-24px))] flex-col p-0">
        <div className="space-y-2 border-b p-3">
          <p className="text-sm font-semibold">{t('order.voucher.customerVouchers')}</p>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('order.voucher.searchPlaceholder')} className="h-8 pl-8 text-xs" />
          </div>
          <p className="text-[10.5px] text-muted-foreground">{t('order.voucher.posRule')}</p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          {groups.length === 0 && <p className="py-4 text-center text-xs text-muted-foreground">{t('order.voucher.noMatch')}</p>}
          {groups.map((g) => (
            <div key={g.title} className="space-y-1.5">
              <p className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">{g.title}</p>
              {g.items.map((v) => {
                const isOn = selected.includes(v.code)
                const shipOnly = v.kind === 'Transport' && delivery !== 'ExpressDelivery'
                return (
                  <div key={v.code} className={cn('rounded-lg border p-2.5 text-xs', isOn && 'border-primary bg-primary/5', shipOnly && 'opacity-60')}>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug">{v.name}</p>
                        <p className="text-[11.5px] font-medium text-primary">{v.value}</p>
                        <p className="mt-0.5 flex flex-wrap gap-x-2 text-[10.5px] text-muted-foreground">
                          <span className="break-all font-mono">{v.code}</span>
                          {v.minOrder ? <span>{t('order.voucher.minOrder', { amount: formatVnd(v.minOrder) })}</span> : null}
                          {v.endDate ? <span>{t('order.voucher.expiry', { date: new Date(v.endDate).toLocaleDateString(currentLocale()) })}</span> : null}
                        </p>
                        {shipOnly && <p className="mt-0.5 text-[10.5px] text-muted-foreground">{t('order.voucher.expressOnly')}</p>}
                      </div>
                      {v.collected ? (
                        <Button size="sm" variant={isOn ? 'default' : 'outline'} className="h-7 shrink-0 px-2 text-[11px]" disabled={shipOnly} onClick={() => onToggle(v)}>
                          {isOn ? t('order.voucher.unselect') : t('common:actions.apply')}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 shrink-0 px-2 text-[11px]" disabled={collect.isPending || shipOnly} onClick={() => onCollect(v)}>
                          {collect.isPending && collect.variables === v.code ? <Loader2 className="animate-spin" /> : null}
                          {v.pointCost ? t('order.voucher.redeem', { points: v.pointCost }) : t('order.voucher.collect')}
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

function PointsSection({ customer, hasCustomer, quote, on, onChange, disabled }: {
  customer: EvotechOrderOptions['customer']
  hasCustomer: boolean
  quote?: CheckoutQuote
  on: boolean
  onChange: (v: boolean) => void
  disabled: boolean
}) {
  const { t } = useTranslation('convCommerce')
  const fmt = (n: number) => new Intl.NumberFormat(currentLocale()).format(n)
  const balance = customer.state === 'linked' || customer.state === 'found' ? customer.points : null
  const usable = quote?.pointsUsable ?? 0
  const can = hasCustomer && usable > 0
  return (
    <section className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="evo-use-points" className="flex items-center gap-1.5 text-xs"><Coins className="h-3.5 w-3.5" /> {t('order.points.label')}</Label>
        <Switch id="evo-use-points" checked={on && can} onCheckedChange={onChange} disabled={disabled || !can} />
      </div>
      <p className="text-[11px] text-muted-foreground">
        {disabled && balance != null
          ? t('order.points.addProducts', { balance: fmt(balance) })
          : !hasCustomer
          ? t('order.points.noAccount')
          : usable <= 0
            ? t('order.points.unusable', { balance: balance != null ? fmt(balance) : 0 })
            : on && quote
              ? balance != null
                ? t('order.points.deductWithLeft', { usable: fmt(usable), discount: formatVnd(quote.pointDiscount), left: fmt(Math.max(0, balance - usable)) })
                : t('order.points.deduct', { usable: fmt(usable), discount: formatVnd(quote.pointDiscount) })
              : balance != null
                ? t('order.points.maxWithBalance', { usable: fmt(usable), balance: fmt(balance) })
                : t('order.points.max', { usable: fmt(usable) })}
      </p>
    </section>
  )
}

function Totals({ lines, quote, loading, error, newAddress }: {
  lines: Line[]
  quote?: CheckoutQuote
  loading: boolean
  error: string | null
  newAddress: boolean
}) {
  const { t } = useTranslation('convCommerce')
  const fallback = lines.reduce((s, l) => s + (l.product.price ?? 0) * l.quantity, 0)
  const row = (label: string, v: string, tone?: 'discount') => (
    <div className="flex items-baseline justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('tabular-nums', tone === 'discount' && 'text-success')}>{v}</span>
    </div>
  )
  return (
    <div className="space-y-1">
      {row(t('order.totals.subtotal'), formatVnd(quote?.subtotal ?? fallback))}
      {quote && quote.comboDiscount > 0 && row(t('order.totals.promotion'), `−${formatVnd(quote.comboDiscount)}`, 'discount')}
      {quote && quote.promotionDiscount > 0 && row(t('order.totals.voucherDiscount'), `−${formatVnd(quote.promotionDiscount)}`, 'discount')}
      {quote && row(t('order.totals.shipping'), quote.shippingFee > 0 ? formatVnd(quote.shippingFee) : t('picker.price.free'))}
      {quote && quote.shippingDiscount > 0 && row(t('order.totals.shippingDiscount'), `−${formatVnd(quote.shippingDiscount)}`, 'discount')}
      {quote && quote.pointDiscount > 0 && row(t('order.totals.pointsDeduct', { points: new Intl.NumberFormat(currentLocale()).format(quote.pointsUsable) }), `−${formatVnd(quote.pointDiscount)}`, 'discount')}
      {quote && quote.tax > 0 && row(t('order.totals.tax'), formatVnd(quote.tax))}
      <div className="flex items-baseline justify-between pt-1 text-sm font-semibold">
        <span className="flex items-center gap-1.5">{t('order.totals.total')} {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}</span>
        <span className="tabular-nums">{formatVnd(quote?.total ?? fallback)}</span>
      </div>
      {quote && quote.pointsEarned > 0 && <p className="text-right text-[10.5px] text-muted-foreground">{t('order.totals.pointsEarned', { points: new Intl.NumberFormat(currentLocale()).format(quote.pointsEarned) })}</p>}
      {error ? (
        <p className="text-[11px] text-destructive">{error}</p>
      ) : newAddress && quote ? (
        <p className="text-[10.5px] text-muted-foreground">{t('order.totals.newAddressNote')}</p>
      ) : !quote && lines.length > 0 && !loading ? (
        <p className="text-[10.5px] text-muted-foreground">{t('order.totals.estimateNote')}</p>
      ) : null}
    </div>
  )
}
